import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { tasksApi } from '@/services/tasks'
import { invalidateNotificationQueries } from '@/hooks/useNotifications'
import type {
  ApiList,
  ApiResponse,
  TaskListQuery,
  Task,
  TaskDetail,
  CreateTaskRequest,
  EditTaskRequest,
  UpdateTaskStatusRequest,
  TaskChecklistItem,
  CreateChecklistItemRequest,
  UpdateChecklistItemRequest,
  CreateTaskCommentRequest,
} from '@/types'

export function useTasksList(
  q: TaskListQuery,
  options?: { paginate?: boolean; enabled?: boolean }
) {
  return useQuery({
    queryKey: ['tasks', 'list', q, options?.paginate ?? true],
    queryFn: () => tasksApi.list(q, options),
    enabled: options?.enabled ?? true,
  })
}

export function useInfiniteTasksList(
  q: TaskListQuery,
  options?: { enabled?: boolean; pageSize?: number }
) {
  const pageSize = options?.pageSize ?? 20

  return useInfiniteQuery({
    queryKey: ['tasks', 'infinite-list', q, pageSize],
    queryFn: ({ pageParam }) =>
      tasksApi.list(
        { ...q, page: pageParam, limit: pageSize },
        { paginate: true }
      ),
    initialPageParam: 1,
    getNextPageParam: last => {
      const { current_page, last_page } = last.metadata
      return current_page < last_page ? current_page + 1 : undefined
    },
    enabled: options?.enabled ?? true,
  })
}

export function useTasksSummary() {
  return useQuery({
    queryKey: ['tasks', 'summary'],
    queryFn: () => tasksApi.summary(),
  })
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'detail', id],
    queryFn: () => tasksApi.get(id as string),
    enabled: !!id,
  })
}

export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTaskRequest) => tasksApi.create(body),
    onSuccess: res => {
      if (res.data?.id) {
        qc.setQueryData(
          ['tasks', 'detail', res.data.id],
          res as unknown as ApiResponse<TaskDetail>
        )
      }
      qc.invalidateQueries({ queryKey: ['tasks', 'list'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      invalidateNotificationQueries(qc)
    },
  })
}

export function useUpdateTask(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditTaskRequest) => tasksApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'list'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', id] })
      invalidateNotificationQueries(qc)
    },
  })
}

export function useUpdateTaskStatus(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateTaskStatusRequest) =>
      tasksApi.updateStatus(id, body),
    onMutate: async body => {
      await Promise.all([
        qc.cancelQueries({ queryKey: ['tasks', 'list'] }),
        qc.cancelQueries({ queryKey: ['tasks', 'detail', id] }),
      ])

      const previousTaskLists = qc.getQueriesData<ApiList<Task>>({
        queryKey: ['tasks', 'list'],
      })
      const previousTaskDetail = qc.getQueryData<ApiResponse<TaskDetail>>([
        'tasks',
        'detail',
        id,
      ])
      const updatedAt = new Date().toISOString()

      qc.setQueriesData<ApiList<Task>>({ queryKey: ['tasks', 'list'] }, old => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map(task =>
            task.id === id
              ? { ...task, status: body.status, updated_at: updatedAt }
              : task
          ),
        }
      })

      qc.setQueryData<ApiResponse<TaskDetail>>(['tasks', 'detail', id], old => {
        if (!old?.data) return old
        return {
          ...old,
          data: { ...old.data, status: body.status, updated_at: updatedAt },
        }
      })

      return { previousTaskLists, previousTaskDetail }
    },
    onError: (_error, _body, context) => {
      context?.previousTaskLists.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data)
      })
      qc.setQueryData(['tasks', 'detail', id], context?.previousTaskDetail)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'list'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', id] })
    },
  })
}

export function useDeleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => tasksApi.remove(id),
    onMutate: async id => {
      await Promise.all([
        qc.cancelQueries({ queryKey: ['tasks', 'list'] }),
        qc.cancelQueries({ queryKey: ['tasks', 'detail', id] }),
      ])

      const previousTaskLists = qc.getQueriesData<ApiList<Task>>({
        queryKey: ['tasks', 'list'],
      })
      const previousTaskDetail = qc.getQueryData<ApiResponse<TaskDetail>>([
        'tasks',
        'detail',
        id,
      ])

      qc.setQueriesData<ApiList<Task>>({ queryKey: ['tasks', 'list'] }, old => {
        if (!old?.data) return old
        const nextData = old.data.filter(task => task.id !== id)
        return {
          ...old,
          data: nextData,
          metadata: old.metadata
            ? {
                ...old.metadata,
                total: Math.max(0, old.metadata.total - 1),
                to:
                  old.metadata.to === null
                    ? null
                    : Math.max(old.metadata.from ?? 0, old.metadata.to - 1),
              }
            : old.metadata,
        }
      })
      qc.removeQueries({ queryKey: ['tasks', 'detail', id], exact: true })

      return { previousTaskLists, previousTaskDetail }
    },
    onError: (_error, id, context) => {
      context?.previousTaskLists.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data)
      })
      qc.setQueryData(['tasks', 'detail', id], context?.previousTaskDetail)
    },
    onSettled: (_data, _error, id) => {
      qc.invalidateQueries({ queryKey: ['tasks', 'list'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.removeQueries({ queryKey: ['tasks', 'detail', id], exact: true })
      qc.removeQueries({ queryKey: ['tasks', 'checklist', id], exact: true })
      qc.removeQueries({ queryKey: ['tasks', 'comments', id], exact: true })
      qc.removeQueries({ queryKey: ['tasks', 'files', id], exact: true })
    },
  })
}

// --- To-do checklist (feedback #69) ---

export function useTaskChecklist(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'checklist', taskId],
    queryFn: () => tasksApi.listChecklist(taskId as string),
    enabled: !!taskId,
  })
}

export function useAddChecklistItem(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateChecklistItemRequest) =>
      tasksApi.addChecklistItem(taskId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'checklist', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
    },
  })
}

export function useUpdateChecklistItem(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      itemId,
      body,
    }: {
      itemId: string
      body: UpdateChecklistItemRequest
    }) => tasksApi.updateChecklistItem(taskId, itemId, body),
    onMutate: async ({ itemId, body }) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: ['tasks', 'checklist', taskId] }),
        qc.cancelQueries({ queryKey: ['tasks', 'detail', taskId] }),
      ])

      const previousChecklist = qc.getQueryData<
        ApiResponse<TaskChecklistItem[]>
      >(['tasks', 'checklist', taskId])
      const previousTaskDetail = qc.getQueryData<ApiResponse<TaskDetail>>([
        'tasks',
        'detail',
        taskId,
      ])

      const applyPatch = (item: TaskChecklistItem): TaskChecklistItem =>
        item.id === itemId ? { ...item, ...body } : item

      qc.setQueryData<ApiResponse<TaskChecklistItem[]>>(
        ['tasks', 'checklist', taskId],
        old => {
          if (!old?.data) return old
          return { ...old, data: old.data.map(applyPatch) }
        }
      )

      qc.setQueryData<ApiResponse<TaskDetail>>(
        ['tasks', 'detail', taskId],
        old => {
          if (!old?.data?.checklist_items) return old
          return {
            ...old,
            data: {
              ...old.data,
              checklist_items: old.data.checklist_items.map(applyPatch),
            },
          }
        }
      )

      return { previousChecklist, previousTaskDetail }
    },
    onError: (_error, _variables, context) => {
      qc.setQueryData(
        ['tasks', 'checklist', taskId],
        context?.previousChecklist
      )
      qc.setQueryData(['tasks', 'detail', taskId], context?.previousTaskDetail)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'checklist', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
    },
  })
}

export function useRemoveChecklistItem(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) =>
      tasksApi.removeChecklistItem(taskId, itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'checklist', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
    },
  })
}

// --- Comments (feedback #71, add-only) ---

export function useTaskComments(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'comments', taskId],
    queryFn: () => tasksApi.listComments(taskId as string),
    enabled: !!taskId,
  })
}

export function useAddTaskComment(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTaskCommentRequest) =>
      tasksApi.addComment(taskId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['tasks', 'comments', taskId] }),
  })
}

// --- Attachments (feedback #70) ---

export function useTaskFiles(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'files', taskId],
    queryFn: () => tasksApi.listFiles(taskId as string),
    enabled: !!taskId,
  })
}

export function useUploadTaskFile(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => tasksApi.uploadFile(taskId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'files', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
    },
  })
}

export function useRemoveTaskFile(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fileId: string) => tasksApi.removeFile(taskId, fileId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'files', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
    },
  })
}

export function useRenameTaskFile(taskId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId, name }: { fileId: string; name: string }) =>
      tasksApi.renameFile(taskId, fileId, name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'files', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
    },
  })
}
